/**
 * Error Reporting Composable
 * Provides reusable error handling functionality for consistent error management across the app
 *
 * Three types of errors:
 * 1. Fatal - Shows Error.vue page and stops execution (e.g., auth failure, critical API failure)
 * 2. Non-fatal - Shows ErrorBanner at top of page (e.g., form submission error, data load error)
 * 3. Silent - Only logs to console (e.g., background sync failure, analytics error)
 */

import { useRouter } from 'vue-router';
import { useAppStore } from '@/stores/useAppStore';
import { reportErrorSpan } from '@/honeycomb';

/**
 * Parse error response to extract useful information
 * Handles different error response formats from API
 *
 * @param {Error} error - The error object
 * @returns {Object} Parsed error details
 */
function parseErrorDetails(error) {
    const meta = error?.response?.data?.meta;

    return {
        message: meta?.message || error.message || 'An unexpected error occurred',
        exceptionType: meta?.exception_type,
        problemId: meta?.problem_identifier,
        requestId: meta?.request_id,
    };
}

/**
 * Build error message text for banner display
 * Formats error details into a user-friendly string
 *
 * @param {Object} details - Parsed error details
 * @returns {string} Formatted error message
 */
function buildErrorMessage(details) {
    const parts = [details.message];

    if (details.problemId) {
        parts.push(`Problem ID: ${details.problemId}`);
    }

    if (details.requestId) {
        parts.push(`Request ID: ${details.requestId}`);
    }

    return parts.join(' | ');
}

/**
 * Convert value to Error object
 * Ensures we always work with Error instances
 *
 * @param {*} value - Value to convert (string, Error, or other)
 * @returns {Error} Error object
 */
function getError(value) {
    return value instanceof Error ? value : new Error(value);
}

/**
 * Report error by logging it with a kind label
 * This allows error tracking services to capture it
 *
 * @param {Error} error - Error to report
 * @param {string} kind - Error kind ('fatal', 'non-fatal', 'silent')
 */
function reportError(error, kind) {
    reportErrorSpan(error, kind);

    // Log error asynchronously so error tracking services can capture it
    // setTimeout ensures it doesn't interfere with current execution
    setTimeout(() => {
        console.error(`[Error: ${kind}]`, error);
    }, 1);
}

/**
 * Main composable hook for error reporting
 * Use this in components or stores that need error handling
 *
 * @returns {Object} Error reporting methods
 */
export function useErrorReporting() {
    const appStore = useAppStore();
    const router = useRouter();

    /**
     * Report a fatal error
     * Shows Error.vue page and navigates to /error route
     * Use for: authentication failures, critical API errors, missing required data
     *
     * @param {Error|string} value - Error value
     * @param {Object} options - Additional error options
     * @param {string} options.title - Error title for Error.vue
     * @param {string} options.message - Error message for Error.vue
     * @param {Object} options.primaryButton - Optional button config
     * @param {string} options.image - Optional image name (discoSad, meiStressed, zoeConcerned)
     */
    function reportFatalError(value, options = {}) {
        const error = getError(value);
        reportError(error, 'fatal');

        const details = parseErrorDetails(error);

        // Set error in store (triggers Error.vue display)
        appStore.updateError({
            title: options.title || 'Something went wrong',
            message: options.message || details.message,
            primaryButton: options.primaryButton,
            image: options.image,
        });

        // Navigate to error page
        router.push({ name: 'error' });
    }

    /**
     * Report a non-fatal error
     * Shows ErrorBanner at top of app
     * Use for: form validation errors, API failures where user can retry, data load errors
     *
     * @param {Error|string} value - Error value
     * @param {Object} options - Additional error options
     * @param {string} options.title - Error title (defaults to generic message)
     * @param {string} options.color - Banner color (defaults to 'error')
     */
    function reportNonFatalError(value, options = {}) {
        const error = getError(value);
        reportError(error, 'non-fatal');

        const details = parseErrorDetails(error);
        const message = buildErrorMessage(details);

        appStore.setBannerError({
            title: options.title || 'There was a problem. Please try again.',
            message,
            color: options.color || 'error',
        });
    }

    /**
     * Report a silent error
     * Only logs to console, no UI change
     * Use for: background sync failures, analytics errors, non-critical issues
     *
     * @param {Error|string} value - Error value
     */
    function reportSilentError(value) {
        const error = getError(value);
        reportError(error, 'silent');
    }

    /**
     * Clear banner error
     * Call this when user dismisses the ErrorBanner
     */
    function clearBannerError() {
        appStore.clearBannerError();
    }

    return {
        reportFatalError,
        reportNonFatalError,
        reportSilentError,
        clearBannerError,
    };
}
